import React from 'react';
import {
  Box, Typography, TextField, Switch, Chip, Select, MenuItem,
  Button, Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  BullFlagIcon, DoubleBottomIcon, BreakoutIcon, RetestIcon,
  TightBaseIcon, VCPIcon, AscendingTriangleIcon, CupHandleIcon,
  InverseHSIcon, BullPennantIcon, ShakeoutIcon, MAPullbackIcon,
  ThrowbackIcon, WyckoffSpringIcon, AscendingBaseIcon, getPatternOptionsWithBadges,
} from './PatternIcons';

/* ── Shared styles ── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff', borderRadius: '6px', height: 40,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 },
  },
  '& .MuiInputBase-input': {
    fontSize: '0.875rem', fontWeight: 700, fontFamily: 'ui-monospace, monospace',
    py: 0, height: 40, textAlign: 'center', color: '#1e293b',
  },
};

const selectSx = {
  bgcolor: '#fff', borderRadius: '6px', height: 40,
  '& .MuiSelect-select': {
    fontSize: '0.8rem', fontWeight: 600, py: 0, height: 40,
    display: 'flex', alignItems: 'center', color: '#1e293b',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea', borderWidth: 2 },
};

const sectionLabel = {
  fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
  color: '#64748b', letterSpacing: '0.05em',
};

const FieldLabel = ({ label, tipKey, t }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
    <Typography sx={{ fontWeight: 600, fontSize: '0.72rem', color: '#475569' }}>{label}</Typography>
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-line', fontSize: 11.5, lineHeight: 1.5, p: 0.5 }}>{t(tipKey)}</Box>}
      arrow placement="top"
      enterTouchDelay={0} leaveTouchDelay={3000}
      slotProps={{ tooltip: { sx: { maxWidth: 260, bgcolor: '#1e293b', borderRadius: '8px', px: 1.5, py: 1 } } }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help', '&:hover': { color: '#667eea' }, transition: 'color 0.15s' }} />
    </Tooltip>
  </Box>
);

const ScannerFilters = ({ filters, onChange, loading, patternPerformance }) => {
  const { t } = useTranslation();

  const basePatternOptions = [
    { value: 'TIGHT_BASE_BREAKOUT', label: 'Base Breakout', icon: <BreakoutIcon fontSize="small" /> },
    { value: 'BULL_FLAG', label: 'Cờ Tăng Giá', icon: <BullFlagIcon fontSize="small" /> },
    { value: 'DOUBLE_BOTTOM', label: 'Đáy Đôi', icon: <DoubleBottomIcon fontSize="small" /> },
    { value: 'BREAKOUT_RETEST', label: 'Retest', icon: <RetestIcon fontSize="small" /> },
    { value: 'TIGHT_BASE_NEW', label: 'Tight Base', icon: <TightBaseIcon fontSize="small" /> },
    { value: 'VCP', label: 'VCP', icon: <VCPIcon fontSize="small" /> },
    { value: 'ASCENDING_TRIANGLE', label: 'Tam Giác Tăng', icon: <AscendingTriangleIcon fontSize="small" /> },
    { value: 'CUP_HANDLE', label: 'Cup & Handle', icon: <CupHandleIcon fontSize="small" /> },
    { value: 'INVERSE_HS', label: 'Đầu Vai Ngược', icon: <InverseHSIcon fontSize="small" /> },
    { value: 'BULL_PENNANT', label: 'Cờ Tam Giác', icon: <BullPennantIcon fontSize="small" /> },
    { value: 'SHAKEOUT', label: 'Shakeout', icon: <ShakeoutIcon fontSize="small" /> },
    { value: 'MA_PULLBACK', label: 'MA Pullback', icon: <MAPullbackIcon fontSize="small" /> },
    { value: 'THROWBACK', label: 'Throwback', icon: <ThrowbackIcon fontSize="small" /> },
    { value: 'WYCKOFF_SPRING', label: 'Wyckoff Spring', icon: <WyckoffSpringIcon fontSize="small" /> },
    { value: 'ASCENDING_BASE', label: 'Nâng Nền', icon: <AscendingBaseIcon fontSize="small" /> },
  ];

  const perfData = getPatternOptionsWithBadges(patternPerformance);
  const patternOptions = basePatternOptions.map(b => {
    const p = perfData.find(x => x.value === b.value);
    return { ...b, label: p?.label || b.label, tier: p?.tier || null, tooltip: p?.tooltip || '' };
  });

  const tierBadge = () => null;

  const isAllSelected = filters.patterns.length === patternOptions.length;

  const SwitchRow = ({ label, checked, onChg, disabled, tipKey }) => (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      py: 0.75, px: 1.5, borderRadius: '6px', bgcolor: '#fff',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: checked ? '#1e293b' : '#94a3b8' }}>{label}</Typography>
        {tipKey && (
          <Tooltip
            title={<Box sx={{ whiteSpace: 'pre-line', fontSize: 11.5, lineHeight: 1.5, p: 0.5 }}>{t(tipKey)}</Box>}
            arrow placement="top"
            enterTouchDelay={0} leaveTouchDelay={3000}
            slotProps={{ tooltip: { sx: { maxWidth: 280, bgcolor: '#1e293b', borderRadius: '8px', px: 1.5, py: 1 } } }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help', '&:hover': { color: '#667eea' }, transition: 'color 0.15s' }} />
          </Tooltip>
        )}
      </Box>
      <Switch checked={checked} onChange={onChg} disabled={disabled} size="small"
        sx={{
          width: 38, height: 22, p: 0,
          '& .MuiSwitch-switchBase': { p: 0, m: '3px', '&.Mui-checked': { transform: 'translateX(16px)', color: '#fff', '& + .MuiSwitch-track': { bgcolor: '#667eea', opacity: 1 } } },
          '& .MuiSwitch-thumb': { width: 16, height: 16 },
          '& .MuiSwitch-track': { borderRadius: 11, bgcolor: '#cbd5e1', opacity: 1 },
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ═══ PATTERNS ═══ */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: '#667eea' }} />
            <Typography sx={sectionLabel}>{t('scanner.filters.patterns')}</Typography>
            <Chip label={`${filters.patterns.length}/${patternOptions.length}`} size="small" sx={{
              height: 22, fontSize: '0.72rem', fontWeight: 700,
              bgcolor: isAllSelected ? '#667eea' : '#e2e8f0',
              color: isAllSelected ? '#fff' : '#64748b',
            }} />
          </Box>
          <Button size="small" onClick={() => onChange({ ...filters, patterns: isAllSelected ? [] : patternOptions.map(o => o.value) })}
            disabled={loading}
            startIcon={isAllSelected ? <CheckBoxIcon sx={{ fontSize: '1rem !important' }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: '1rem !important' }} />}
            sx={{
              fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', py: 0.25, px: 1,
              minHeight: 28, borderRadius: 1,
              color: isAllSelected ? '#fff' : '#64748b',
              bgcolor: isAllSelected ? '#667eea' : '#fff',
              border: isAllSelected ? 'none' : '1px solid #e2e8f0',
              '&:hover': { bgcolor: isAllSelected ? '#5568d3' : '#f8fafc' },
            }}>
            {isAllSelected ? t('scanner.filters.deselectAll') : t('scanner.filters.selectAll')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {patternOptions.map(o => {
            const isSel = filters.patterns.includes(o.value);
            return (
              <Tooltip key={o.value} title={o.tooltip ? <Box sx={{ whiteSpace: 'pre-line', fontSize: 11 }}>{o.tooltip}</Box> : ''} arrow disableHoverListener={!o.tooltip}>
                <Box onClick={() => !loading && onChange({
                  ...filters,
                  patterns: isSel ? filters.patterns.filter(p => p !== o.value) : [...filters.patterns, o.value],
                })}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.25, py: 0.75, cursor: loading ? 'not-allowed' : 'pointer',
                    bgcolor: isSel ? '#667eea' : '#f8fafc',
                    border: isSel ? '1.5px solid #667eea' : '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    '&:hover': { bgcolor: isSel ? '#5568d3' : '#eef0ff', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                    transition: 'all 0.15s',
                  }}>
                  <Box sx={{ color: isSel ? '#fff' : '#667eea', display: 'flex', '& svg': { fontSize: '0.9rem' } }}>{o.icon}</Box>
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap',
                    color: isSel ? '#fff' : '#334155', display: 'flex', alignItems: 'center',
                  }}>
                    {t(`patterns.${o.value.toLowerCase()}`, o.label)}
                    {tierBadge(o.tier, isSel)}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* ═══ SCORING & FILTER ═══ */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
          <TuneIcon sx={{ fontSize: 18, color: '#667eea' }} />
          <Typography sx={sectionLabel}>{t('scanner.filters.conditions')}</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          <Box>
            <FieldLabel label={t('scanner.filters.patternScore')} tipKey="scanner.filters.patternScoreTip" t={t} />
            <TextField type="number" value={filters.min_score}
              onChange={e => onChange({ ...filters, min_score: parseFloat(e.target.value) })}
              disabled={loading} size="small" fullWidth
              InputProps={{ inputProps: { min: 0, max: 10, step: 0.5 } }} sx={inputSx} />
          </Box>
          <Box>
            <FieldLabel label={t('scanner.filters.volumeScore')} tipKey="scanner.filters.volumeScoreTip" t={t} />
            <TextField type="number" value={filters.min_volume_score}
              onChange={e => onChange({ ...filters, min_volume_score: parseFloat(e.target.value) })}
              disabled={loading || !filters.use_volume_analysis} size="small" fullWidth
              InputProps={{ inputProps: { min: 0, max: 10, step: 0.5 } }}
              sx={{ ...inputSx, opacity: filters.use_volume_analysis ? 1 : 0.4 }} />
          </Box>
          <Box>
            <FieldLabel label={t('scanner.filters.liquidity')} tipKey="scanner.filters.liquidityTip" t={t} />
            <Select value={filters.min_liquidity}
              onChange={e => onChange({ ...filters, min_liquidity: parseFloat(e.target.value) })}
              disabled={loading} fullWidth size="small" sx={selectSx}>
              <MenuItem value={0}>{t('scanner.filters.liq.all')}</MenuItem>
              <MenuItem value={1000000000}>{t('scanner.filters.liq.1b')}</MenuItem>
              <MenuItem value={2000000000}>{t('scanner.filters.liq.2b')}</MenuItem>
              <MenuItem value={5000000000}>{t('scanner.filters.liq.5b')}</MenuItem>
              <MenuItem value={10000000000}>{t('scanner.filters.liq.10b')}</MenuItem>
              <MenuItem value={20000000000}>{t('scanner.filters.liq.20b')}</MenuItem>
              <MenuItem value={50000000000}>{t('scanner.filters.liq.50b')}</MenuItem>
            </Select>
          </Box>
          <Box>
            <FieldLabel label={t('scanner.filters.maxResults')} tipKey="scanner.filters.maxResultsTip" t={t} />
            <TextField type="number" value={filters.max_results}
              onChange={e => onChange({ ...filters, max_results: parseInt(e.target.value, 10) })}
              disabled={loading} size="small" fullWidth
              InputProps={{ inputProps: { min: 10, max: 100, step: 10 } }} sx={inputSx} />
          </Box>
        </Box>
      </Box>

      {/* ═══ OPTIONS ═══ */}
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <SwitchRow label={t('scanner.filters.volumeAnalysis')} checked={filters.use_volume_analysis}
            onChg={e => onChange({ ...filters, use_volume_analysis: e.target.checked })} disabled={loading}
            tipKey="scanner.filters.volumeAnalysisTip" />
          <SwitchRow label={t('scanner.filters.weeklyGate')} checked={filters.use_weekly_gate}
            onChg={e => onChange({ ...filters, use_weekly_gate: e.target.checked })} disabled={loading}
            tipKey="scanner.filters.weeklyGateTip" />
          <SwitchRow label={t('scanner.filters.vnFilters')} checked={filters.use_vn_filters}
            onChg={e => onChange({ ...filters, use_vn_filters: e.target.checked })} disabled={loading}
            tipKey="scanner.filters.vnFiltersTip" />
        </Box>
      </Box>

      {/* ═══ MODE ═══ */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {[
          { val: 'breakout', label: t('scanner.filters.breakoutLabel'), icon: <RocketLaunchIcon sx={{ fontSize: 16 }} />, desc: t('scanner.filters.breakoutShortDesc') },
          { val: 'forming', label: t('scanner.filters.formingLabel'), icon: <VisibilityIcon sx={{ fontSize: 16 }} />, desc: t('scanner.filters.formingShortDesc') },
        ].map(m => {
          const active = filters.pattern_mode === m.val;
          return (
            <Box key={m.val} onClick={() => !loading && onChange({ ...filters, pattern_mode: m.val })}
              sx={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 1,
                py: 1.25, px: 1.5, borderRadius: '6px', cursor: 'pointer',
                bgcolor: active ? '#667eea' : '#f8fafc',
                border: active ? '1.5px solid #667eea' : '1.5px solid #e2e8f0',
                '&:hover': { bgcolor: active ? '#5568d3' : '#eef0ff' },
                transition: 'all 0.15s',
              }}>
              <Box sx={{ color: active ? '#fff' : '#667eea' }}>{m.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: active ? '#fff' : '#1e293b', lineHeight: 1.2 }}>{m.label}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: active ? 'rgba(255,255,255,0.7)' : '#94a3b8', lineHeight: 1.2 }}>{m.desc}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ScannerFilters;

